import { useState, useMemo, Fragment } from 'react';
import type { ReactNode } from 'react';

import { EmptyState, Icon, Pagination } from '@repo/ui';
import { ErrorCard } from './ErrorCard';

// ============================================================================
// Types and Interfaces
// ============================================================================

export interface DataTableColumn<T = any> {
  /** Unique identifier for the column */
  key: string;
  /** Header label */
  header: string | ReactNode;
  /** Alignment */
  align?: 'left' | 'right' | 'center';
  /** Width class */
  width?: string;
  /** Header cell className */
  headerClassName?: string;
  /** Custom header renderer */
  renderHeader?: () => ReactNode;
  /** Cell renderer */
  renderCell: (row: T, index: number) => ReactNode;
  /** Column className */
  className?: string;
}

export interface DataTablePagination {
  page: number;
  limit: number;
}

export interface DataTablePaginatedResponse<T> {
  data: T[];
  total: number;
  page?: number;
  limit?: number;
  totals?: any;
  hasMore?: boolean;
  summary?: any;
}

export type RowKeyGenerator<T = any> = (row: T, index: number) => string;

export type ExpandedRowRenderer<T = any> = (row: T, index: number) => ReactNode;

export interface DataTableProps<T = any> {
  /** Table columns */
  columns: DataTableColumn<T>[];
  /** Data fetch function or hook result */
  data?: DataTablePaginatedResponse<T> | null;
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: Error | null;
  /** Retry callback for error state */
  onRetry?: () => void;
  /** Current pagination */
  pagination?: DataTablePagination;
  /** Pagination change handler */
  onPaginationChange?: (pagination: DataTablePagination) => void;
  /** Empty state props */
  emptyState?: {
    icon?: string;
    title?: string;
    description?: string;
  };
  /** Table container className */
  tableContainerClassName?: string;
  /** Table className */
  tableClassName?: string;
  /** Enable sticky header */
  stickyHeader?: boolean;
  /** Table max height */
  tableMaxHeight?: string;
  /** Page size options */
  pageSizeOptions?: number[];
  /** Custom header section (filters, summary cards, etc.) */
  renderHeader?: () => ReactNode;
  /** Custom action section (export button, etc.) - rendered at top right of table */
  renderAction?: () => ReactNode;
  /** Hide pagination */
  hidePagination?: boolean;
  /** Row key generator */
  getRowKey?: RowKeyGenerator<T>;
  /** Expanded row content renderer */
  renderExpandedRow?: ExpandedRowRenderer<T>;
  /** Function to determine if row can be expanded */
  isRowExpandable?: (row: T, index: number) => boolean;
  /** Row click handler */
  onRowClick?: (row: T, index: number) => void;
  /** Custom row className */
  getRowClassName?: (row: T, index: number) => string;
  /** Loading skeleton row count */
  loadingRowCount?: number;
  /** Loading skeleton column count */
  loadingColumnCount?: number;
}

// ============================================================================
// DataTable Component
// ============================================================================

/**
 * Generic DataTable component for displaying paginated, filterable data
 * Supports expandable rows, custom filters, and more
 * Can be used across the entire application for any table needs
 */
export function DataTable<T = any>({
  columns,
  data,
  isLoading = false,
  error = null,
  onRetry,
  pagination,
  onPaginationChange,
  emptyState = {
    icon: 'HiUsers',
    title: 'No data available',
    description: 'There are no items matching your current filters.',
  },
  tableContainerClassName = '',
  tableClassName = 'min-w-full border-collapse',
  stickyHeader = false,
  tableMaxHeight,
  pageSizeOptions = [25, 50, 100, 200],
  renderHeader,
  renderAction,
  hidePagination = false,
  getRowKey = (row: T, index: number) => {
    if (row && typeof row === 'object') {
      return (row as any).id || (row as any).clientId || (row as any).billId || `row-${index}`;
    }
    return `row-${index}`;
  },
  renderExpandedRow,
  isRowExpandable,
  onRowClick,
  getRowClassName,
  loadingRowCount = 5,
  loadingColumnCount = 7,
}: DataTableProps<T>) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRowExpansion = (rowKey: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowKey)) {
      newExpanded.delete(rowKey);
    } else {
      newExpanded.add(rowKey);
    }
    setExpandedRows(newExpanded);
  };

  const tableData = data?.data || [];
  const hasData = tableData.length > 0;
  const showTable = !isLoading && data && hasData;
  const showPagination =
    !hidePagination && !isLoading && data && data.total > 0 && pagination && onPaginationChange;

  const containerClass = useMemo(() => {
    let baseClass = 'overflow-x-auto border border-xtnd-dark-100 dark:border-xtnd-dark-600';
    // Only round top corners when pagination is shown at bottom, keep bottom flat for seamless connection with pagination
    if (showPagination) {
      baseClass += ' rounded-t-lg';
    } else {
      baseClass += ' rounded-lg';
    }
    if (tableMaxHeight) {
      baseClass += ` overflow-y-auto relative`;
      if (stickyHeader) {
        baseClass += ' min-h-[700px]';
      }
    }
    return `${baseClass} ${tableContainerClassName}`;
  }, [tableMaxHeight, stickyHeader, tableContainerClassName, showPagination]);

  const tableWrapperStyle = useMemo(() => {
    if (tableMaxHeight) {
      return { maxHeight: tableMaxHeight };
    }
    return undefined;
  }, [tableMaxHeight]);

  const headerBaseClass = useMemo(
    () =>
      stickyHeader
        ? 'bg-xtnd-light dark:bg-xtnd-dark sticky top-0 z-30 shadow-xtnd-md'
        : 'bg-xtnd-dark-50 dark:bg-xtnd-dark-600 shadow-xtnd',
    [stickyHeader]
  );

  return (
    <div className="flex flex-col">
      {renderHeader && <div className="mb-6 mt-6 flex-shrink-0">{renderHeader()}</div>}

      {/* Action Section - Above table, aligned to the right */}
      {showTable && renderAction && <div className="flex justify-end mb-2">{renderAction()}</div>}

      {isLoading && (
        <div className="animate-pulse flex-shrink-0">
          <div className="grid grid-cols-1 gap-4 mb-4">
            {Array.from({ length: loadingColumnCount }).map((_, i) => (
              <div key={i} className="h-4 bg-xtnd-dark-100 dark:bg-xtnd-dark-700 rounded"></div>
            ))}
          </div>
          {Array.from({ length: loadingRowCount }).map((_, i) => (
            <div key={i} className="grid grid-cols-1 gap-4 mb-3">
              {Array.from({ length: loadingColumnCount }).map((_, j) => (
                <div key={j} className="h-6 bg-xtnd-dark-100 dark:bg-xtnd-dark-700 rounded"></div>
              ))}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-6 flex-shrink-0">
          <ErrorCard
            title="Error loading data"
            message={error.message || 'An error occurred while loading the data.'}
            onRetry={onRetry}
          />
        </div>
      )}

      {!isLoading && !error && (!data || !hasData) && (
        <div className="flex-shrink-0">
          <EmptyState
            icon={(emptyState.icon as any) || 'HiUsers'}
            title={emptyState.title || 'No data available'}
            description={
              emptyState.description || 'There are no items matching your current filters.'
            }
            variant="outlined"
            padding="lg"
          />
        </div>
      )}

      {/* Table Container */}
      {showTable && (
        <div className="relative">
          <div className={containerClass} style={tableWrapperStyle}>
            <table className={tableClassName}>
              <thead className={headerBaseClass}>
                <tr>
                  {renderExpandedRow && (
                    <th className="w-10 px-4 py-3 border-b border-r border-xtnd-dark-100 dark:border-xtnd-dark-600"></th>
                  )}
                  {columns.map((column, colIndex) => {
                    const alignClass =
                      column.align === 'right'
                        ? 'text-right'
                        : column.align === 'center'
                          ? 'text-center'
                          : 'text-left';
                    const isLastColumn = colIndex === columns.length - 1;
                    const headerClass = `px-6 py-3 ${alignClass} text-xs font-medium text-xtnd-dark dark:text-xtnd-white uppercase tracking-wider border-b border-xtnd-dark-100 dark:border-xtnd-dark-600 ${
                      !isLastColumn ? 'border-r' : ''
                    } ${column.width || ''} ${column.headerClassName || ''}`;

                    return (
                      <th key={column.key} className={headerClass}>
                        {column.renderHeader ? column.renderHeader() : column.header}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-xtnd-dark-700">
                {tableData.map((row, index) => {
                  const rowKey = getRowKey(row, index);
                  const isExpanded = expandedRows.has(rowKey);
                  const canExpand = isRowExpandable ? isRowExpandable(row, index) : true;
                  const rowClassName = getRowClassName
                    ? getRowClassName(row, index)
                    : 'hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600 transition-colors duration-150';
                  const rowClickHandler = onRowClick
                    ? (e: React.MouseEvent) => {
                        // Don't trigger row click if clicking on expand cell
                        const target = e.target as HTMLElement;
                        const expandCell = target.closest('td[data-expand-cell="true"]');
                        if (!expandCell) {
                          onRowClick(row, index);
                        }
                      }
                    : undefined;

                  return (
                    <Fragment key={rowKey}>
                      <tr
                        className={`${rowClassName} ${onRowClick ? 'cursor-pointer' : ''}`}
                        onClick={rowClickHandler}
                      >
                        {renderExpandedRow && (
                          <td
                            className={`w-14 px-2 py-3 whitespace-nowrap border-b border-r border-xtnd-dark-100 dark:border-xtnd-dark-600 ${
                              canExpand
                                ? 'cursor-pointer hover:bg-xtnd-dark-50 dark:hover:bg-xtnd-dark-600 transition-colors'
                                : ''
                            }`}
                            onClick={canExpand ? e => toggleRowExpansion(rowKey, e) : undefined}
                            role={canExpand ? 'button' : undefined}
                            aria-label={
                              canExpand ? (isExpanded ? 'Collapse row' : 'Expand row') : undefined
                            }
                            data-expand-cell="true"
                          >
                            {canExpand && (
                              <div className="flex items-center justify-center">
                                <Icon
                                  name={isExpanded ? 'HiChevronDown' : 'HiChevronRight'}
                                  className="h-5 w-5 text-xtnd-dark-300"
                                />
                              </div>
                            )}
                          </td>
                        )}
                        {columns.map((column, colIndex) => {
                          const alignClass =
                            column.align === 'right'
                              ? 'text-right'
                              : column.align === 'center'
                                ? 'text-center'
                                : 'text-left';
                          const isLastColumn = colIndex === columns.length - 1;
                          const cellClass = `px-6 py-4 whitespace-nowrap ${alignClass} border-b border-xtnd-dark-100 dark:border-xtnd-dark-600 ${
                            !isLastColumn ? 'border-r' : ''
                          } ${column.className || ''}`;

                          return (
                            <td key={column.key} className={cellClass}>
                              {column.renderCell(row, index)}
                            </td>
                          );
                        })}
                      </tr>

                      {renderExpandedRow && isExpanded && canExpand && (
                        <tr className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700">
                          <td
                            colSpan={columns.length + 1}
                            className="px-6 py-4 border-b border-xtnd-dark-100 dark:border-xtnd-dark-600"
                          >
                            {renderExpandedRow(row, index)}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {showPagination && pagination && (
            <div className="border-b border-l border-r border-xtnd-dark-100 dark:border-xtnd-dark-600 rounded-b-lg overflow-hidden bg-white dark:bg-xtnd-dark-700">
              <Pagination
                currentPage={pagination.page}
                totalPages={Math.ceil(data.total / pagination.limit)}
                totalRecords={data.total}
                hasNextPage={pagination.page < Math.ceil(data.total / pagination.limit)}
                hasPreviousPage={pagination.page > 1}
                isLoading={isLoading}
                pageSize={pagination.limit}
                pageSizeOptions={pageSizeOptions}
                onPageChange={page => onPaginationChange({ ...pagination, page })}
                onPageSizeChange={pageSize =>
                  onPaginationChange({ ...pagination, page: 1, limit: pageSize })
                }
                showPageSizeSelector={true}
                showRecordCount={true}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
